import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Typography } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useNurseJobNotificationsQuery } from "../../../../../hooks/useNurseJobNotifications";
import { FONT_SIZE, ICON_SIZE, INK } from "../../../../../theme/tokens";
import type { JobNotificationState } from "../../../../../utils/jobNotificationTypes";
import { ChangesSource, type Job } from "../../../../../utils/models";
import { JobNotificationChip } from "./JobNotificationChip";
import { getJobChipsView, orderJobChips } from "./jobChipsView";
import { useJobNotificationChipToggle } from "./useJobNotificationChipToggle";

/** The employee changed it from the app. The automatic mute on a new job doesn't count. */
const isEmployeeChange = (state: JobNotificationState | undefined): boolean =>
	state !== undefined && !state.isDefault && state.updatedSource === ChangesSource.NurseApp;

type Props = {
	departmentId: string;
	nurseId: number;
	firstName: string;
	/** The employee's jobs in saved order. The first is the primary job. */
	jobs: Job[];
	canToggle: boolean;
};

const summarySx = { fontSize: FONT_SIZE.CAPTION, lineHeight: 1.4, fontWeight: 500 } as const;

/**
 * View-mode Job field: one chip per job, primary first.
 * With five or more jobs it shows three plus "+N more", so the card stays small.
 * The line below shows the count while chips are hidden, or a warning when no job notifies.
 */
export const JobNotificationChips: FC<Props> = ({ departmentId, nurseId, firstName, jobs, canToggle }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const { stateByJobId, silentByJobId, isError, refetch, isRefetching } = useNurseJobNotificationsQuery(departmentId, nurseId);
	const toggle = useJobNotificationChipToggle({ departmentId, nurseId, firstName });

	const view = useMemo(() => {
		const named = jobs.map((job) => ({ id: job.id, displayName: job.displayName }));
		return getJobChipsView(orderJobChips(named, jobs[0]?.id, silentByJobId), expanded);
	}, [jobs, silentByJobId, expanded]);

	return (
		<Box data-testid="profile-job-notification-chips" sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxWidth: "100%" }}>
				{view.visible.map((chip) => (
					<JobNotificationChip
						key={chip.id}
						jobName={chip.displayName}
						isPrimary={chip.isPrimary}
						isSilent={chip.isSilent}
						changedByEmployee={isEmployeeChange(stateByJobId.get(chip.id))}
						canToggle={canToggle}
						onToggle={() => chip.isSilent !== undefined && toggle(chip.id, chip.displayName, chip.isSilent)}
						dataTestId={`profile-job-chip-${chip.id}`}
					/>
				))}
				{view.isCollapsible && (
					<MetaChip
						label={expanded ? t("show.less") : t("jobNotifications.profile.showMore", { count: view.hiddenCount })}
						tone="primary"
						size="medium"
						onClick={() => setExpanded((open) => !open)}
						dataTestId={expanded ? "profile-job-chips-show-less" : "profile-job-chips-show-more"}
						sx={{ borderColor: "transparent", fontWeight: 500 }}
					/>
				)}
			</Box>
			{view.allSilent && (
				<Typography
					role="status"
					data-testid="profile-job-notifications-all-silent"
					sx={{ ...summarySx, display: "flex", alignItems: "center", gap: 0.5, color: "warning.dark" }}
				>
					<WarningAmberRoundedIcon aria-hidden sx={{ fontSize: ICON_SIZE.PILL }} />
					{t("jobNotifications.profile.allSilent")}
				</Typography>
			)}
			{view.showCountLine && (
				<Typography data-testid="profile-job-notifications-count" sx={{ ...summarySx, color: INK.MUTED }}>
					<Box component="span" sx={{ color: INK.SECONDARY, fontWeight: 600 }}>
						{t("jobNotifications.profile.jobCount", { count: view.totalCount })}
					</Box>
					{` · ${t("jobNotifications.profile.notifyingCount", { count: view.notifyingCount })}`}
				</Typography>
			)}
			{isError && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
					<Typography role="alert" sx={{ ...summarySx, color: "error.dark" }}>
						{t("jobNotifications.profile.loadFailed")}
					</Typography>
					<SystemButton
						buttonVariant="borderless"
						size="compact"
						noFullWidthMobile
						loading={isRefetching}
						onClick={() => refetch()}
						data-testid="profile-job-notifications-retry"
						sx={{ color: "primary.main" }}
					>
						{t("retry")}
					</SystemButton>
				</Box>
			)}
		</Box>
	);
};
